import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Star, MapPin, Phone, Truck } from "lucide-react";

const REMOVALS_IMG = "https://images.unsplash.com/photo-1620455800201-7f00aeef12ed?crop=entropy&cs=srgb&fm=jpg&w=1200&q=70&ixlib=rb-4.1.0";

export default function RemovalCompanies() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [area, setArea] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const fetchIt = () => {
    const p = new URLSearchParams();
    if (q) p.set("q", q);
    if (area) p.set("area", area);
    if (verifiedOnly) p.set("verified", "true");
    api.get(`/removal-companies?${p.toString()}`).then(({ data }) => setItems(data.items || []));
  };

  useEffect(() => { fetchIt(); /* eslint-disable-next-line */ }, []);

  return (
    <div>
      <section className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-8 items-end">
          <div>
            <div className="overline text-safety">Directory</div>
            <h1 className="mt-3 font-display font-black text-4xl lg:text-6xl leading-none tracking-tighter text-forest">
              UK commercial<br/>removal firms
            </h1>
            <p className="mt-6 text-muted-foreground max-w-lg leading-relaxed">
              Vetted, rated and ready. Browse specialist commercial movers for office
              clearances, IT disposal and full site reinstatement.
            </p>
            <div className="mt-8 grid grid-cols-1 md:grid-cols-[1fr,220px,auto] gap-2 border border-forest max-w-xl">
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Search removal firms"
                data-testid="removals-search-q"
                className="border-0 focus-visible:ring-0 shadow-none rounded-none h-12"
              />
              <Input
                value={area} onChange={(e) => setArea(e.target.value)}
                placeholder="Service area"
                data-testid="removals-search-area"
                className="border-0 focus-visible:ring-0 shadow-none rounded-none h-12"
              />
              <Button onClick={fetchIt} data-testid="removals-search-btn" className="rounded-none bg-forest hover:bg-forest-800 text-white h-12 px-6">Find</Button>
            </div>
            <label className="mt-4 inline-flex items-center gap-2 cursor-pointer text-sm">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={(e) => { setVerifiedOnly(e.target.checked); setTimeout(fetchIt, 0); }}
                data-testid="removals-verified-filter"
                className="accent-forest"
              />
              <span className="flex items-center gap-1.5">Verified firms only <span className="bg-forest text-white overline text-[9px] px-1.5 py-0.5">✓</span></span>
            </label>
          </div>
          <div className="hidden lg:block relative aspect-[4/3] border border-border overflow-hidden">
            <img src={REMOVALS_IMG} alt="Removal van loading" className="absolute inset-0 w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-10">
        {items.length === 0 ? (
          <div className="border border-border bg-white p-16 text-center">
            <div className="overline text-muted-foreground mb-3">No results</div>
            <div className="font-display text-2xl tracking-tight text-forest">No companies match your search</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {items.map((c) => (
              <Link
                to={`/removal-companies/${c.company_id}`}
                key={c.company_id}
                data-testid={`removal-card-${c.company_id}`}
                className="group grid grid-cols-1 md:grid-cols-[220px,1fr,180px] gap-6 border border-border bg-white p-6 hover:shadow-lg transition-transform duration-200 hover:-translate-y-0.5"
              >
                <div className="aspect-[4/3] md:aspect-square bg-forest text-white grid place-items-center relative overflow-hidden">
                  <Truck className="w-12 h-12 opacity-90" />
                  <div className="absolute bottom-2 left-2 overline text-white/80 text-[10px]">Verified partner</div>
                </div>
                <div>
                  <div className="font-display text-2xl tracking-tight text-forest group-hover:text-safety transition-colors duration-200">{c.name}</div>
                  <p className="mt-2 text-sm text-muted-foreground line-clamp-2 leading-relaxed max-w-xl">{c.description}</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {c.services?.slice(0, 4).map((s) => (
                      <span key={s} className="text-xs border border-border px-2 py-1 text-muted-foreground">{s}</span>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{c.service_areas?.slice(0, 2).join(", ")}</span>
                    <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5" />{c.phone}</span>
                  </div>
                </div>
                <div className="flex md:flex-col justify-between md:justify-end items-start md:items-end gap-3 border-t md:border-t-0 md:border-l border-border pt-4 md:pt-0 md:pl-6">
                  {c.rating > 0 && (
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 fill-safety text-safety" />
                      <span className="font-display text-lg tabular-nums">{c.rating.toFixed(1)}</span>
                      <span className="overline text-xs text-muted-foreground">({c.review_count})</span>
                    </div>
                  )}
                  {c.hourly_rate && (
                    <div className="text-right">
                      <div className="overline text-muted-foreground">From</div>
                      <div className="font-display text-2xl text-forest tabular-nums">£{c.hourly_rate}<span className="text-sm text-muted-foreground">/hr</span></div>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
